// Re-fetch on an interval while something on screen is still in flight, and
// stop the moment nothing is. The single seam for "live" state in the SPA
// (Migration seams: swapping this for API Gateway WebSockets or an SSE Lambda
// is one file). Every page that waits on a job calls this instead of hand-
// rolling its own setInterval.

import { useEffect, useRef } from "react";

/** 3 s: fast enough that a text job (2-6 s) resolves on screen; ~20 req/min
 *  against a warm Lambda is free at this scale. */
export const POLL_INTERVAL_MS = 3000;
/** 15 min: stop polling a job that has outlived the SQS visibility timeout -
 *  it is the reaper's problem now, not this tab's. */
export const POLL_MAX_MS = 900_000;

interface PollOptions {
  /** Poll only while this is true; going false clears the timer immediately. */
  enabled: boolean;
  intervalMs?: number;
  maxMs?: number;
}

/**
 * Calls `fn` every `intervalMs` while `enabled`, stopping on unmount, when
 * `enabled` goes false, and after `maxMs` has elapsed since polling began.
 * `fn` is read through a ref so a fresh closure each render does not restart
 * the interval - only `enabled`/`intervalMs`/`maxMs` do.
 */
export function usePolling(fn: () => void | Promise<void>, { enabled, intervalMs = POLL_INTERVAL_MS, maxMs = POLL_MAX_MS }: PollOptions): void {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) return;
    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - startedAt >= maxMs) {
        clearInterval(timer);
        return;
      }
      void fnRef.current();
    }, intervalMs);
    return () => clearInterval(timer);
  }, [enabled, intervalMs, maxMs]);
}
